import type { PlayerParty } from '@/api/public'

export type PlayerPartyVersion = {
  partyID: string
  revision: string
  etag: string
}

export type PlayerPartyVersionIssue =
  | 'invalid_party_identity'
  | 'missing'
  | 'unexpected'
  | 'weak'
  | 'list'
  | 'unquoted'
  | 'mismatch'

export type PlayerPartyVersionValidation =
  | {
      valid: true
      version: PlayerPartyVersion | null
      source: 'header' | 'json' | 'solo'
    }
  | {
      valid: false
      version: null
      issue: PlayerPartyVersionIssue
    }

type PlayerPartyVersionSources = {
  headerETag?: string | null
  jsonETag?: string | null
}

const positiveDecimalPattern = /^[1-9][0-9]*$/

export function validatePlayerPartyVersion(
  party: PlayerParty | null,
  sources: PlayerPartyVersionSources = {},
): PlayerPartyVersionValidation {
  const headerETag = normalizePartyHeaderETagCandidate(sources.headerETag)
  const jsonETag = exactPartyJSONETagCandidate(sources.jsonETag)
  const jsonETagProvided = sources.jsonETag !== undefined
  if (!party) {
    if (
      headerETag ||
      (jsonETagProvided && sources.jsonETag !== null)
    ) {
      return { valid: false, version: null, issue: 'unexpected' }
    }
    return { valid: true, version: null, source: 'solo' }
  }

  if (
    typeof party.id !== 'string' ||
    typeof party.revision !== 'string'
  ) {
    return { valid: false, version: null, issue: 'invalid_party_identity' }
  }
  const partyID = party.id.trim()
  const revision = party.revision.trim()
  if (
    !partyID ||
    partyID !== party.id ||
    partyID.includes('"') ||
    /[\u0000-\u001f\u007f]/.test(partyID) ||
    revision !== party.revision ||
    !positiveDecimalPattern.test(revision)
  ) {
    return { valid: false, version: null, issue: 'invalid_party_identity' }
  }

  const expected = `"party:${partyID}:${revision}"`
  if (jsonETagProvided) {
    if (jsonETag === expected) {
      return {
        valid: true,
        version: { partyID, revision, etag: expected },
        source: 'json',
      }
    }
    return {
      valid: false,
      version: null,
      issue: classifyInvalidPartyETag(jsonETag),
    }
  }
  if (headerETag === expected) {
    return {
      valid: true,
      version: { partyID, revision, etag: expected },
      source: 'header',
    }
  }

  return {
    valid: false,
    version: null,
    issue: classifyInvalidPartyETag(headerETag),
  }
}

export function isPartyConcurrencyError(error: unknown): boolean {
  if (!isStructuredRequestError(error)) return false
  return (
    error.status === 412 ||
    error.status === 428 ||
    error.code === 'invalid_party_precondition' ||
    error.code === 'party_precondition_required' ||
    error.code === 'party_revision_conflict'
  )
}

function normalizePartyHeaderETagCandidate(
  raw: string | null | undefined,
): string {
  return typeof raw === 'string' ? raw.trim() : ''
}

function exactPartyJSONETagCandidate(
  raw: string | null | undefined,
): string {
  return typeof raw === 'string' ? raw : ''
}

function classifyInvalidPartyETag(
  candidate: string,
): PlayerPartyVersionIssue {
  if (!candidate) return 'missing'
  if (candidate.startsWith('W/')) return 'weak'
  if (candidate.includes(',')) return 'list'
  if (!candidate.startsWith('"') || !candidate.endsWith('"')) {
    return 'unquoted'
  }
  return 'mismatch'
}

function isStructuredRequestError(
  error: unknown,
): error is Error & { status: number; code: string | null } {
  return (
    error instanceof Error &&
    typeof (error as { status?: unknown }).status === 'number' &&
    (
      (error as { code?: unknown }).code === null ||
      typeof (error as { code?: unknown }).code === 'string'
    )
  )
}
